/**
 * Utility functions for keystroke-level input restrictions.
 */

export const lettersOnlyRegex = /^[a-zA-Z\s.,'()/+-]*$/;
export const lettersAndSpacesRegex = /^[a-zA-Z\s]*$/;
// Allows letters, spaces, dot, apostrophe, hyphen, but no consecutive special chars (like -- or ..)
export const nameRegex = /^(?!.*--)(?!.*\.\.)(?!.*'')[a-zA-Z\s.'-]*$/;
export const digitsOnlyRegex = /^\d*$/;
export const decimalRegex = /^\d*(\.\d{0,2})?$/;
export const percentageRegex = /^\d*(\.\d{0,2})?%?$/;
export const addressRegex = /^[a-zA-Z0-9\s.,#/-]*$/;
export const textRegex = /^[a-zA-Z0-9\s.,'()/+-]*$/;

/**
 * Creates an onChange handler that restricts input based on a regex and max length.
 * If the input is valid, it calls the original handleChange (e.g. from Formik).
 * 
 * @param {Function} handleChange - The original Formik handleChange function.
 * @param {RegExp} regex - The regular expression to test the new value against.
 * @param {number} [maxLength] - Optional maximum length for the input.
 * @returns {Function} - The new onChange event handler.
 */
export const createRestrictedHandler = (handleChange, regex, maxLength) => {
  return (e) => {
    const value = e.target.value;
    if (maxLength && value.length > maxLength) {
      return;
    }
    if (value === '' || regex.test(value)) {
      handleChange(e);
    }
  };
};

/**
 * Restricts the keystrokes for an MUI Autocomplete component by intercepting its inputProps.onChange.
 * Usage: {...restrictAutocomplete(params, regex)} inside renderInput.
 */
export const restrictAutocomplete = (params, regex) => {
  if (!params) return params;
  
  // Clone the params object to avoid mutating MUI's internal references
  const newParams = { ...params };
  
  if (newParams.inputProps) {
    const originalOnChange = newParams.inputProps.onChange;
    newParams.inputProps = {
      ...newParams.inputProps,
      onChange: (e) => {
        if (e.target.value === '' || regex.test(e.target.value)) {
          if (originalOnChange) originalOnChange(e);
        }
      }
    };
  } else if (newParams.slotProps && newParams.slotProps.htmlInput) {
    const originalOnChange = newParams.slotProps.htmlInput.onChange;
    newParams.slotProps = {
      ...newParams.slotProps,
      htmlInput: {
        ...newParams.slotProps.htmlInput,
        onChange: (e) => {
          if (e.target.value === '' || regex.test(e.target.value)) {
            if (originalOnChange) originalOnChange(e);
          }
        }
      }
    };
  }
  
  return newParams;
};
